import { create } from 'zustand';
import { AnnualTamPrimeAggregate, LifetimeMarginCumulAggregate } from '../types/primes';

export type AnnualTamPrimesCacheEntry = {
  aggregate: AnnualTamPrimeAggregate;
  lastFetchedAt: number;
};

export type LifetimeMarginCacheEntry = {
  cumul: LifetimeMarginCumulAggregate;
  lastFetchedAt: number;
};

export type LifetimeMarginCumulsBatchCacheEntry = {
  cumuls: LifetimeMarginCumulAggregate[];
  lastFetchedAt: number;
};

export type AnnualTamPrimesState = {
  byKey: Record<string, AnnualTamPrimesCacheEntry>;
  lifetimeByKey: Record<string, LifetimeMarginCacheEntry>;
  lifetimeCumulsBatch: LifetimeMarginCumulsBatchCacheEntry | null;
  getCache: (cacheKey: string) => AnnualTamPrimesCacheEntry | null;
  setCache: (cacheKey: string, aggregate: AnnualTamPrimeAggregate) => void;
  invalidateCache: (cacheKey: string) => void;
  getLifetimeCache: (cacheKey: string) => LifetimeMarginCacheEntry | null;
  setLifetimeCache: (cacheKey: string, cumul: LifetimeMarginCumulAggregate) => void;
  invalidateLifetimeCache: (cacheKey: string) => void;
  getLifetimeCumulsBatchCache: () => LifetimeMarginCumulsBatchCacheEntry | null;
  setLifetimeCumulsBatchCache: (cumuls: LifetimeMarginCumulAggregate[]) => void;
  invalidateLifetimeCumulsBatchCache: () => void;
};

export const useAnnualTamPrimesStore = create<AnnualTamPrimesState>()((set, get) => ({
  byKey: {},
  lifetimeByKey: {},
  lifetimeCumulsBatch: null,
  getCache: (cacheKey) => get().byKey[cacheKey] ?? null,
  setCache: (cacheKey, aggregate) =>
    set((state) => ({
      byKey: {
        ...state.byKey,
        [cacheKey]: {
          aggregate,
          lastFetchedAt: Date.now(),
        },
      },
    })),
  invalidateCache: (cacheKey) =>
    set((state) => {
      if (!(cacheKey in state.byKey)) {
        return state;
      }

      const nextByKey = { ...state.byKey };
      delete nextByKey[cacheKey];
      return { byKey: nextByKey };
    }),
  getLifetimeCache: (cacheKey) => get().lifetimeByKey[cacheKey] ?? null,
  setLifetimeCache: (cacheKey, cumul) =>
    set((state) => ({
      lifetimeByKey: {
        ...state.lifetimeByKey,
        [cacheKey]: {
          cumul,
          lastFetchedAt: Date.now(),
        },
      },
    })),
  invalidateLifetimeCache: (cacheKey) =>
    set((state) => {
      if (!(cacheKey in state.lifetimeByKey)) {
        return state;
      }

      const nextLifetimeByKey = { ...state.lifetimeByKey };
      delete nextLifetimeByKey[cacheKey];
      return { lifetimeByKey: nextLifetimeByKey };
    }),
  getLifetimeCumulsBatchCache: () => get().lifetimeCumulsBatch,
  setLifetimeCumulsBatchCache: (cumuls) =>
    set({
      lifetimeCumulsBatch: {
        cumuls,
        lastFetchedAt: Date.now(),
      },
    }),
  invalidateLifetimeCumulsBatchCache: () =>
    set((state) => (state.lifetimeCumulsBatch === null ? state : { lifetimeCumulsBatch: null })),
}));
